import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, Compass } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'

/**
 * Branded 404. Rendered as the catch-all <Route> at the end of the router,
 * so any path that doesn't match a real route lands here with site chrome
 * + a useful set of links instead of a stale SPA shell.
 */
export function NotFoundPage() {
  const { pathname } = useLocation()

  return (
    <SiteLayout>
      <div className="mx-auto flex max-w-3xl flex-col items-start gap-6 px-4 py-20 sm:py-28 sm:px-6">
        <span className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--color-accent)] font-[var(--font-mono)]">
          <Compass size={12} aria-hidden />
          404 · Off the map
        </span>

        <h1 className="text-[44px] sm:text-[56px] font-semibold tracking-tight text-[var(--color-text-strong)] leading-[1.05]">
          404 Not Found
        </h1>
        <p className="text-[20px] sm:text-[22px] font-medium text-[var(--color-text-strong)] leading-snug">
          That coordinate is outside our coverage.
        </p>

        <p className="text-[15.5px] leading-relaxed text-[var(--color-muted)]">
          The page <code className="font-[var(--font-mono)] text-[13.5px] text-[var(--color-text-strong)]">{pathname}</code>{' '}
          doesn't exist on geoborder.us. Try one of these instead.
        </p>

        <div className="grid w-full gap-3 sm:grid-cols-2">
          <Link
            to="/"
            className={[
              'block rounded-[var(--radius-lg)] p-5',
              'border border-[var(--color-border)] bg-[var(--color-elev)]',
              'transition-colors hover:border-[var(--color-accent)]/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
            ].join(' ')}
          >
            <h2 className="text-[14px] font-semibold text-[var(--color-text-strong)]">
              Homepage map
            </h2>
            <p className="mt-1.5 text-[13px] text-[var(--color-muted)]">
              Click anywhere on the world to see its jurisdictional stack.
            </p>
          </Link>

          <Link
            to="/api"
            className={[
              'block rounded-[var(--radius-lg)] p-5',
              'border border-[var(--color-border)] bg-[var(--color-elev)]',
              'transition-colors hover:border-[var(--color-accent)]/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
            ].join(' ')}
          >
            <h2 className="text-[14px] font-semibold text-[var(--color-text-strong)]">
              API docs
            </h2>
            <p className="mt-1.5 text-[13px] text-[var(--color-muted)]">
              Bearer-key authenticated API at <code className="font-[var(--font-mono)] text-[12px]">api.geoborder.us/v1</code>.
            </p>
          </Link>
        </div>

        <Link
          to="/"
          className={[
            'inline-flex items-center gap-1.5 mt-2 text-[14px] font-medium',
            'text-[var(--color-accent)] hover:underline underline-offset-2',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)] rounded',
          ].join(' ')}
        >
          <ArrowLeft size={14} aria-hidden />
          Back to map
        </Link>
      </div>
    </SiteLayout>
  )
}
